import { create } from 'zustand';
import { message } from 'antd';
import axiosInstance from '../utils/axiosInstance';
import { getCachedData, setCachedData, clearCacheByKey } from '../utils/cacheUtil';

const useBookingStore = create((set) => ({
  bookings: [],
  loading: false,
  error: null,

  fetchBookings: async (filters = {}) => {
    const queryParams = new URLSearchParams(filters).toString();
    const cacheKey = `bookings-${queryParams}`;
    
    const cached = getCachedData(cacheKey);
    if (cached) {
      set({ bookings: cached, loading: false, error: null });
      return;
    }

    set({ loading: true });
    try {
      const response = await axiosInstance.get(`/bookings/${queryParams ? `?${queryParams}` : ''}`);
      setCachedData(cacheKey, response.data);
      set({ 
        bookings: response.data,
        loading: false,
        error: null 
      });
    } catch (error) {
      console.error('Error fetching bookings:', error);
      set({ loading: false });
      
      // Extract the error message more safely
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to fetch bookings';
      
      message.error(errorMessage);
      set({ error: errorMessage });
    }
  },

  createBooking: async (bookingData) => {
    set({ loading: true });
    try {
      const response = await axiosInstance.post('/bookings/', bookingData);
      // Clear all booking-related cache entries
      Array.from(Array(31)).forEach((_, i) => {
        clearCacheByKey(`bookings-${i}`);
      });
      clearCacheByKey('bookings');
      set(state => ({
        bookings: [...state.bookings, response.data],
        loading: false,
      }));
      message.success('Booking created successfully');
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error creating booking:', error);
      set({ loading: false });
      
      // Extract the error message more safely
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to create booking';
      
      message.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  },

  updateBooking: async (id, bookingData) => {
    set({ loading: true });
    try {
      const response = await axiosInstance.put(`/bookings/${id}/`, bookingData);
      Array.from(Array(31)).forEach((_, i) => {
        clearCacheByKey(`bookings-${i}`);
      });
      clearCacheByKey('bookings');
      set(state => ({
        bookings: state.bookings.map(booking => 
          booking.id === id ? response.data : booking
        ),
        loading: false,
      }));
      message.success('Booking updated successfully');
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error updating booking:', error);
      set({ loading: false });
      
      // Extract the error message more safely
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to update booking';
      
      message.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  },

  deleteBooking: async (id) => {
    try {
      await axiosInstance.delete(`/bookings/${id}/`);
      Array.from(Array(31)).forEach((_, i) => {
        clearCacheByKey(`bookings-${i}`);
      });
      clearCacheByKey('bookings');
      set(state => ({
        bookings: state.bookings.filter(booking => booking.id !== id),
      }));
      message.success('Booking deleted successfully');
      return { success: true };
    } catch (error) {
      console.error('Error deleting booking:', error);
      
      // Extract the error message more safely
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to delete booking';
      
      message.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  },

  getBookingById: (id) => {
    const state = useBookingStore.getState();
    return state.bookings.find(booking => booking.id === id);
  },

  searchBookings: async (query) => {
    set({ loading: true });
    try {
      const response = await axiosInstance.get(`/bookings/?search=${query}`);
      set({ 
        bookings: response.data,
        loading: false,
        error: null 
      });
    } catch (error) {
      console.error('Error searching bookings:', error);
      set({ loading: false });
      
      // Extract the error message more safely
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to search bookings';
      
      message.error(errorMessage);
      set({ error: errorMessage });
    }
  },

  filterBookings: async (filters) => {
    set({ loading: true });
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const response = await axiosInstance.get(`/bookings/?${queryParams}`);
      set({ 
        bookings: response.data,
        loading: false,
        error: null 
      });
    } catch (error) {
      console.error('Error filtering bookings:', error);
      set({ loading: false });
      
      // Extract the error message more safely
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to filter bookings';
      
      message.error(errorMessage);
      set({ error: errorMessage });
    }
  }
}));

export default useBookingStore;
